'use client';

import { useTranslations } from 'next-intl';
import { CrossDark } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { loadLegacyTrackers } from 'lib/analytics/destinations';
import { persistLandingClickIds } from 'lib/analytics/landingParams';
import { resolveAnalyticsStack } from 'lib/analytics/runtime';
import { NextStackBanner } from 'components/shared/consent/NextStackBanner';
import { LEGACY_CONSENT_EVENT } from 'components/shared/consent/useLegacyCookieAccepted';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { usePathname } from 'next/navigation';
import { NO_COOKIE_TRACKING_PAGES } from 'components/shared/constants';
import styles from './CookieConsentBanner.module.css';

const LOCAL_STORAGE_COOKIE_CONSENT_KEY = 'COOKIE_CONSENT';
const SCROLL_HIDE_THRESHOLD_PX = 100;

const CookieConsentContext = createContext<
  readonly [boolean | null | undefined, (v: boolean | null) => unknown]
>([false, () => {}]);

export const CookieConsentBannerContext = createContext<boolean | null>(false);
export const CookieConsentBannerProvider = CookieConsentBannerContext.Provider;

export const CookieConsentProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [cookieConsent, setCookieConsent] = useState<
    boolean | null | undefined
  >(undefined);

  useEffect(() => {
    if (cookieConsent === undefined) {
      const storedConsent = getCookieConsentFromLocalStorage();
      setCookieConsent(storedConsent);
      return;
    }

    if (cookieConsent !== undefined) {
      setCookieConsentToLocalStorage(cookieConsent);
    }
  }, [cookieConsent]);

  const value = useMemo(() => {
    return [cookieConsent, setCookieConsent] as const;
  }, [cookieConsent, setCookieConsent]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};

const useCookieConsentPreference = () => {
  const [cookieConsent, setCookieConsent] = useState<
    boolean | null | undefined
  >(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setCookieConsent((previous) => {
      const storedConsent = getCookieConsentFromLocalStorage();
      return previous === storedConsent ? previous : storedConsent;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (cookieConsent === undefined) return;

    if (cookieConsent) {
      window?.gtag?.('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }

    if (cookieConsent !== null) {
      setCookieConsentToLocalStorage(cookieConsent);
    }
  }, [cookieConsent]);

  const accept = useCallback(() => {
    setCookieConsent(true);
  }, []);

  const reject = useCallback(() => {
    setCookieConsent(false);
  }, []);

  return { cookieConsent, accept, reject } as const;
};

const CookieConsentBannerComponent = ({
  className,
}: {
  className?: string;
}) => {
  const t = useTranslations('common');
  const { cookieConsent, accept, reject } = useCookieConsentPreference();
  const { liteUI } = useGlobalUI();
  const internalPathname = useInternalPathname();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolledBeyondThreshold, setIsScrolledBeyondThreshold] =
    useState(false);

  const [bannerClosedByUser, setBannerClosedByUser] = useState(false);

  const isCycleReport = internalPathname.startsWith('/cycle-report/');
  // Pages that opt out of cookies entirely: no banner AND no tracking scripts,
  // even if consent was granted elsewhere on the site.
  const noCookieTracking = NO_COOKIE_TRACKING_PAGES.some((page) =>
    internalPathname.startsWith(page),
  );
  const shouldRenderBanner = cookieConsent === null && !liteUI;

  useEffect(() => {
    if (!shouldRenderBanner || bannerClosedByUser) {
      setIsScrolledBeyondThreshold(false);
      return;
    }

    if (typeof window === 'undefined') return;

    const evaluateScrollPosition = () => {
      const scrollPosition = Math.max(
        window.scrollY ?? 0,
        window.pageYOffset ?? 0,
        document.documentElement?.scrollTop ?? 0,
        document.body?.scrollTop ?? 0,
      );
      const hasScrolledPast = scrollPosition > SCROLL_HIDE_THRESHOLD_PX;

      setIsScrolledBeyondThreshold((previous) =>
        previous === hasScrolledPast ? previous : hasScrolledPast,
      );
    };

    evaluateScrollPosition();

    const listenerOptions: AddEventListenerOptions = { passive: true };

    window.addEventListener('scroll', evaluateScrollPosition, listenerOptions);

    return () => {
      window.removeEventListener(
        'scroll',
        evaluateScrollPosition,
        listenerOptions,
      );
    };
  }, [shouldRenderBanner]);

  return (
    <>
      {cookieConsent === undefined ||
      bannerClosedByUser ||
      isCycleReport ||
      noCookieTracking ? null : (
        <CSSTransition
          in={shouldRenderBanner}
          timeout={200}
          classNames="fade"
          unmountOnExit
          appear
          nodeRef={containerRef}
        >
          <div
            ref={containerRef}
            className={
              className ? `${styles.root} ${className}` : styles.root
            }
            data-hidden-on-scroll={
              isScrolledBeyondThreshold ? 'true' : 'false'
            }
          >
            <div className={styles.content}>
              <h2 className={styles.heading}>
                {t('cookieBanner.heading')}
                <button
                  className={styles.closeButton}
                  type="button"
                  aria-label={t('cookieBanner.close')}
                  onClick={() => {
                    setBannerClosedByUser(true);
                  }}
                >
                  <CrossDark fill="rgba(255, 255, 255, 0.5)" />
                </button>
              </h2>
              <p className={styles.description}>
                {t('cookieBanner.description')}
                <CustomLink
                  className={styles.policyLink}
                  href="https://ultrahumanapp.notion.site/Cookie-Policy-ebcff6b1c95744fb9471ee874fc544e7"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('cookieBanner.policyLink')}
                </CustomLink>
              </p>
              <span className={styles.actions}>
                <button
                  className={styles.actionButton}
                  type="button"
                  onClick={reject}
                >
                  {t('cookieBanner.REJECT')}
                </button>
                <button
                  className={`${styles.actionButton} ${styles.primary}`}
                  type="button"
                  onClick={accept}
                >
                  {t('cookieBanner.ACCEPT')}
                </button>
              </span>
            </div>
          </div>
        </CSSTransition>
      )}
      {cookieConsent && !noCookieTracking ? <Trackers /> : null}
    </>
  );
};

const CookieConsentBannerSwitch = ({ className }: { className?: string }) => {
  const { store } = useRegionLocale();
  if (resolveAnalyticsStack(store) === 'next') {
    return <NextStackBanner className={className} />;
  }
  return <CookieConsentBannerComponent className={className} />;
};

export const CookieConsentBanner = memo(CookieConsentBannerSwitch);
CookieConsentBanner.displayName = 'CookieConsentBanner';

const getCookieConsentFromLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const item = window.localStorage.getItem(LOCAL_STORAGE_COOKIE_CONSENT_KEY);
  if (item === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(item);
    return parsed;
  } catch (error) {
    console.warn('Failed to parse cookie consent from localStorage:', error);
    return null;
  }
};

const setCookieConsentToLocalStorage = (value: boolean | null) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    LOCAL_STORAGE_COOKIE_CONSENT_KEY,
    JSON.stringify(value),
  );
  if (value === true) persistLandingClickIds();
  window.dispatchEvent(new Event(LEGACY_CONSENT_EVENT));
};

// Reddit's pixel.js does not watch client-side route changes. The first path
// is the landing URL, which loadReddit already counted.
let lastTrackedPath: string | null = null;

const RedditPageVisitOnRouteChange = () => {
  const pathname = usePathname();
  useEffect(() => {
    if (lastTrackedPath === null) {
      lastTrackedPath = pathname;
      return;
    }
    if (pathname === lastTrackedPath) return;
    lastTrackedPath = pathname;
    window.rdt?.('track', 'PageVisit');
  }, [pathname]);
  return null;
};

const Trackers = () => {
  const { region, store } = useRegionLocale();
  const pathname = usePathname();

  useEffect(() => {
    loadLegacyTrackers(region, pathname, store);
  }, [region, pathname, store]);

  return (
    <>
      <div data-trackers="legacy" hidden />
      <RedditPageVisitOnRouteChange />
    </>
  );
};

export default CookieConsentBanner;

export const useScript = (
  attributes: ({ src: string } | { body: string }) & {
    options?: { [key: string]: string };
    head?: boolean;
    onload?: () => void;
  },
) => {
  useEffect(() => {
    const script = document.createElement('script');
    if (attributes.onload)
      script.onload = attributes.onload as (
        this: GlobalEventHandlers,
        ev: Event,
      ) => any;
    script.type = 'text/javascript';
    if ('src' in attributes) {
      script.src = attributes.src;
    } else {
      script.innerHTML = attributes.body;
    }
    if (attributes.options) {
      Object.entries(attributes.options).forEach(([key, value]) => {
        script.setAttribute(key, value as string);
      });
    }

    if (attributes.head) document.body.appendChild(script);
    else document.body.appendChild(script);

    return () => {
      if (attributes.head) document.body.removeChild(script);
      else document.body.removeChild(script);
    };
  }, []);
};
